import { Box, Link } from '@mui/material';

import { usePostCancelModeChange } from '@/api/device/postCancelModeChange';
import { CellularFirmwareSettings, useDevice } from '@/api/devices';
import { ActionDialog } from '@/components/ActionDialog';
import { InlineAlert, InlineAlertBody, InlineAlertPrimaryButton, InlineAlertTitle } from '@/components/ui/InlineAlert';
import { deviceModes } from '@/config';
import { useDisclosure } from '@/hooks/useDisclosure';

export const PendingModeChangeAlert = ({
    readOnly,
    targetModeText,
    staleChangeRequest,
}: {
    readOnly?: boolean;
    targetModeText: React.ReactNode;
    staleChangeRequest: boolean;
}) => {
    if (readOnly || !staleChangeRequest) {
        return (
            <InlineAlert severity="info" sx={{ width: '100%' }}>
                <InlineAlertTitle>Mode configuration change has been sent</InlineAlertTitle>
                <InlineAlertBody>
                    This may take several hours.{' '}
                    {!readOnly && (
                        <>You will not be able to change this Spotter&#39;s mode again until this process completes. </> // there's an intentional trailing space for the children
                    )}
                    Once the process completes, your Spotter will change from its current configuration to{' '}
                    {targetModeText}.
                </InlineAlertBody>
            </InlineAlert>
        );
    }

    return (
        <InlineAlert severity="info" sx={{ width: '100%' }} actionButton={<CancelModeChangeButton />}>
            <InlineAlertTitle>Mode configuration change is taking longer than usual</InlineAlertTitle>
            <InlineAlertBody>
                Your Spotter has not been able to switch to {targetModeText}. You can cancel the mode configuration
                change and try again, making sure that your Spotter is on, charged, and has a clear view of the sky to
                receive the change. View our <TroubleshootingLink /> to learn more.
            </InlineAlertBody>
        </InlineAlert>
    );
};

const TroubleshootingLink = () => (
    <Link
        href="https://sofarocean.notion.site/Spotter-FAQs-e9c6c1b8b9404c288aa699dfbb33b3cb#3738ff9594508083a488f222d5d5b824"
        target="_blank"
        rel="noopener noreferrer"
    >
        troubleshooting documentation
    </Link>
);

const formatRateIntervalFromHours = (hours: number) => {
    if (hours >= 1) {
        return `${hours}-hour`;
    }
    return `${hours * 60}-minute`;
};

export const SatelliteModeChangeText = ({
    targetMode,
    targetMessagePeriod,
}: {
    targetMode?: string | null;
    targetMessagePeriod?: number | null;
}) => {
    if (!targetMode || !targetMessagePeriod) {
        return null;
    }
    const modeLabel = deviceModes.find((mode) => mode.value === targetMode)?.label;
    return (
        <>
            {modeLabel} mode with a {formatRateIntervalFromHours(targetMessagePeriod)} update rate
        </>
    );
};

const formatRateIntervalFromMinutes = (minutes: number) => {
    if (minutes >= 60) {
        return `${minutes / 60}-hour`;
    }
    return `${minutes}-minute`;
};

export const CellularModeChangeText = ({
    currentSettings,
    targetSettings,
}: {
    currentSettings?: CellularFirmwareSettings;
    targetSettings?: CellularFirmwareSettings | null;
}) => {
    if (!currentSettings || !targetSettings) {
        return null;
    }

    // target cellular settings only contain settings that need to be changed, use the current config if the config isn't changing
    const hdrRecordsPerMessage = targetSettings.hdrRecordsPerMessage ?? currentSettings.hdrRecordsPerMessage;
    const outboundNoteSyncPeriodMin =
        targetSettings.outboundNoteSyncPeriodMin ?? currentSettings.outboundNoteSyncPeriodMin!;

    return (
        <>
            {hdrRecordsPerMessage === 0
                ? ' HDR disabled'
                : ` HDR enabled with a ${formatRateIntervalFromMinutes(outboundNoteSyncPeriodMin)} processing rate`}
        </>
    );
};

const CancelModeChangeButton = () => {
    const { data: device } = useDevice();
    const spotterId = device?.spotterId;
    const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useDisclosure();
    const { mutateAsync: cancelModeChange, isPending: pendingMutation } = usePostCancelModeChange();

    const handleConfirm = async () => {
        if (!spotterId) {
            console.warn('No Spotter selected');
            return;
        }

        try {
            await cancelModeChange({
                spotterId: spotterId,
            });
        } catch (err) {
            console.error(err);
        }
        closeConfirmDialog();
    };

    return (
        <>
            <InlineAlertPrimaryButton color="error" variant="outlined" onClick={openConfirmDialog}>
                Cancel change
            </InlineAlertPrimaryButton>
            <ActionDialog
                isLoading={pendingMutation}
                isOpen={isConfirmDialogOpen}
                onReject={closeConfirmDialog}
                onConfirm={handleConfirm}
                title="Are you sure you want to cancel your mode configuration change?"
                confirmLabel="Yes, cancel change"
                confirmButtonProps={{ color: 'error' }}
                rejectLabel="No, go back"
                message={[
                    <>
                        Once you cancel the mode configuration change, you can send a new one. Before you do, make sure
                        that your Spotter:
                        <Box component="ul" sx={{ listStyleType: 'initial', pl: 2.5 }}>
                            <li>is powered on</li>
                            <li>is charged</li>
                            <li>has a clear view of the sky</li>
                        </Box>
                    </>,
                    <>
                        In rare cases, cancelled changes may still get applied later on. To revert these changes, send a
                        new mode configuration change.
                    </>,
                    <>
                        View our <TroubleshootingLink /> for more help.
                    </>,
                ]}
            />
        </>
    );
};
